// src/JournalContext.js
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "./supabaseClient";
import { useAuth } from "./AuthContext";
import { useXP } from "./XPContext";
import { useOnlineStatus } from "./useOnlineStatus";
import { sanitizeText, sanitizeShortText, sanitizeMood, sanitizeSleep, sanitizeWeight, sanitizeDrawing, sanitizeHabitCardColor, sanitizeLocation } from "./sanitize";
import {
  enqueue,
  getQueue,
  clearQueue,
  saveSnapshot,
  loadSnapshot,
  savePendingImage,
  getPendingImages,
  removePendingImage,
} from "./offlineStorage";

const JournalContext = createContext();
export const useJournal = () => useContext(JournalContext);

export const JournalProvider = ({ children }) => {
  const { user } = useAuth();
  const { awardXPForHabit, revokeXPForHabit } = useXP();
  const isOnline = useOnlineStatus();
  const [isSyncing, setIsSyncing] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const stateRef = useRef({});

  // The `location` column was added later (supabase/migrations/20251019000000_journal_entry_location.sql).
  // If the database hasn't been migrated yet, retry the write without it so journaling keeps
  // working — the tag just won't persist until the migration is run.
  const locationColumnMissingRef = useRef(false);
  const upsertJournalEntry = useCallback(async (payload) => {
    const row = { ...payload, updated_at: new Date().toISOString() };
    if (locationColumnMissingRef.current) delete row.location;
    const first = await supabase.from('journal_entries').upsert(row, { onConflict: 'user_id,date_key' });
    if (first.error && !locationColumnMissingRef.current && 'location' in row && /location/i.test(first.error.message || '')) {
      locationColumnMissingRef.current = true;
      if (process.env.NODE_ENV === 'development') console.warn('journal_entries.location column missing; run the location migration.');
      delete row.location;
      return supabase.from('journal_entries').upsert(row, { onConflict: 'user_id,date_key' });
    }
    return first;
  }, []);

  // Daily entries: summary + full journal
  const [entries, setEntries] = useState({});

  // Habit names per month: { "2025-01": ["Exercise", "Read"], ... }
  const [habitsByMonth, setHabitsByMonth] = useState({});
  // Habit reminders per month: { "2025-01": { "Exercise": { enabled: true, time: "09:00" } }, ... }
  const [habitReminders, setHabitReminders] = useState({});
  // Habits hidden from friends: { "2025-01": { "Exercise": true }, ... }
  const [habitsHideFromFriends, setHabitsHideFromFriends] = useState({});
  // Habit card background colors: { "2025-01": { "Exercise": "#aabbcc" }, ... }
  const [habitsBackgroundColors, setHabitsBackgroundColors] = useState({});
  // Which year/month we're currently viewing for habits (set by journal/mobile view)
  const [habitsYear, setHabitsYear] = useState(() => new Date().getFullYear());
  const [habitsMonth, setHabitsMonthState] = useState(() => new Date().getMonth() + 1);

  const yearMonthKey = (y, m) => `${y}-${String(m).padStart(2, "0")}`;
  const currentYearMonthKey = yearMonthKey(habitsYear, habitsMonth);
  const habits = habitsByMonth[currentYearMonthKey] || [];

  const habitsForMonth = useCallback(
    (year, month) => habitsByMonth[yearMonthKey(year, month)] || [],
    [habitsByMonth]
  );

  // Habit completion log: { "2025-01-03": { Workout: true, Read: false } }
  const [habitLog, setHabitLog] = useState({});

  // Sleep log: { "2025-01-03": 7.5 }
  const [sleepLog, setSleepLog] = useState({});

  // Mood log: { "2025-01-03": "green" }
  const [moodLog, setMoodLog] = useState({});

  // Weight log: { "2025-01-03": 165.5 } (stored in lbs)
  const [weightLog, setWeightLog] = useState({});

  // Weight display unit: 'lbs' | 'kg' (persisted in localStorage)
  const [weightUnit, setWeightUnitState] = useState(() => {
    if (typeof localStorage === 'undefined') return 'lbs';
    return localStorage.getItem('weightUnit') || 'lbs';
  });
  const setWeightUnit = useCallback((unit) => {
    setWeightUnitState(unit);
    localStorage.setItem('weightUnit', unit);
  }, []);

  // Loading state
  const [isLoading, setIsLoading] = useState(true);

  // Keep stateRef updated for snapshot saves
  stateRef.current = {
    entries,
    habitsByMonth,
    habitReminders,
    habitsHideFromFriends,
    habitsBackgroundColors,
    habitLog,
    sleepLog,
    moodLog,
    weightLog,
  };

  // Save snapshot when offline (debounced)
  useEffect(() => {
    if (!user || isOnline) return;
    const timer = setTimeout(() => {
      const s = stateRef.current;
      if (s.entries) {
        saveSnapshot(user.id, {
          entries: s.entries,
          habitsByMonth: s.habitsByMonth,
          habitReminders: s.habitReminders,
          habitsHideFromFriends: s.habitsHideFromFriends || {},
          habitsBackgroundColors: s.habitsBackgroundColors || {},
          habitLog: s.habitLog,
          sleepLog: s.sleepLog,
          moodLog: s.moodLog,
          weightLog: s.weightLog,
        }).catch(() => {});
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [entries, habitsByMonth, habitReminders, habitsHideFromFriends, habitsBackgroundColors, habitLog, sleepLog, moodLog, weightLog, user, isOnline]);

  // Update pending count (queue + pending images)
  useEffect(() => {
    const update = async () => {
      try {
        const [q, imgs] = await Promise.all([getQueue(), getPendingImages()]);
        setPendingCount(q.length + imgs.length);
      } catch {}
    };
    update();
    const interval = setInterval(update, 2000);
    return () => clearInterval(interval);
  }, [isOnline, isSyncing]);

  // ===== FETCH DATA FROM SUPABASE =====
  const fetchAllData = useCallback(async () => {
    if (!user) {
      setEntries({});
      setHabitsByMonth({});
      setHabitReminders({});
      setHabitsHideFromFriends({});
      setHabitsBackgroundColors({});
      setHabitLog({});
      setSleepLog({});
      setMoodLog({});
      setWeightLog({});
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    try {
      // When offline, load from local snapshot
      if (!navigator.onLine) {
        const snap = await loadSnapshot(user.id);
        if (snap) {
          setEntries(snap.entries || {});
          setHabitsByMonth(snap.habitsByMonth || {});
          setHabitReminders(snap.habitReminders || {});
          setHabitsHideFromFriends(snap.habitsHideFromFriends || {});
          setHabitsBackgroundColors(snap.habitsBackgroundColors || {});
          setHabitLog(snap.habitLog || {});
          setSleepLog(snap.sleepLog || {});
          setMoodLog(snap.moodLog || {});
          setWeightLog(snap.weightLog || {});
        }
        setIsLoading(false);
        return;
      }
      // Fetch journal entries
      const { data: entriesData } = await supabase
        .from('journal_entries')
        .select('*')
        .eq('user_id', user.id);

      if (entriesData) {
        Object.values(stateRef.current?.entries || {}).forEach((e) => {
          if (e?.imageUrl?.startsWith?.('blob:')) URL.revokeObjectURL(e.imageUrl);
        });
        const entriesMap = {};
        entriesData.forEach(entry => {
          entriesMap[entry.date_key] = {
            summary: entry.summary,
            entry: entry.entry,
            drawing: entry.drawing,
            imageUrl: entry.image_url || null,
            isFavourite: entry.is_favourite === true,
            location: sanitizeLocation(entry.location)
          };
        });
        setEntries(entriesMap);
      }

      // Fetch habits (all months; table has year_month column)
      const { data: habitsData } = await supabase
        .from('habits')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: true });

      if (habitsData && habitsData.length > 0) {
        const byMonth = {};
        const reminders = {};
        const hideFromFriends = {};
        const backgroundColors = {};
        habitsData.forEach((h) => {
          const ym = h.year_month || `${new Date(h.created_at).getFullYear()}-${String(new Date(h.created_at).getMonth() + 1).padStart(2, "0")}`;
          if (!byMonth[ym]) byMonth[ym] = [];
          byMonth[ym].push(h.name);
          if (h.reminder_enabled && h.reminder_time) {
            if (!reminders[ym]) reminders[ym] = {};
            reminders[ym][h.name] = { enabled: true, time: h.reminder_time };
          }
          if (h.hide_from_friends) {
            if (!hideFromFriends[ym]) hideFromFriends[ym] = {};
            hideFromFriends[ym][h.name] = true;
          }
          if (h.card_background_color) {
            const hex = sanitizeHabitCardColor(h.card_background_color);
            if (hex) {
              if (!backgroundColors[ym]) backgroundColors[ym] = {};
              backgroundColors[ym][h.name] = hex;
            }
          }
        });
        setHabitsByMonth(byMonth);
        setHabitReminders(reminders);
        setHabitsHideFromFriends(hideFromFriends);
        setHabitsBackgroundColors(backgroundColors);
      } else {
        setHabitsByMonth({});
        setHabitReminders({});
        setHabitsHideFromFriends({});
        setHabitsBackgroundColors({});
      }

      // Fetch habit log
      const { data: habitLogData } = await supabase
        .from('habit_log')
        .select('*')
        .eq('user_id', user.id);

      if (habitLogData) {
        const logMap = {};
        habitLogData.forEach(log => {
          if (!logMap[log.date_key]) {
            logMap[log.date_key] = {};
          }
          logMap[log.date_key][log.habit_name] = log.completed;
        });
        setHabitLog(logMap);
      }

      // Fetch sleep log
      const { data: sleepData } = await supabase
        .from('sleep_log')
        .select('*')
        .eq('user_id', user.id);

      if (sleepData) {
        const sleepMap = {};
        sleepData.forEach(log => {
          sleepMap[log.date_key] = log.hours;
        });
        setSleepLog(sleepMap);
      }

      // Fetch mood log
      const { data: moodData } = await supabase
        .from('mood_log')
        .select('*')
        .eq('user_id', user.id);

      if (moodData) {
        const moodMap = {};
        moodData.forEach(log => {
          moodMap[log.date_key] = log.mood;
        });
        setMoodLog(moodMap);
      }

      // Fetch weight log
      const { data: weightData } = await supabase
        .from('weight_log')
        .select('*')
        .eq('user_id', user.id);

      if (weightData) {
        const weightMap = {};
        weightData.forEach(log => {
          weightMap[log.date_key] = log.weight;
        });
        setWeightLog(weightMap);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  // Load data when user changes
  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Flush offline queue when back online
  const flushQueue = useCallback(async () => {
    if (!user || !isOnline || isSyncing) return;
    const queue = await getQueue();
    if (queue.length === 0) {
      const pending = await getPendingImages();
      if (pending.length === 0) return;
    }

    setIsSyncing(true);
    try {
      // Upload pending images first
      const pendingImages = await getPendingImages();
      for (const { dateKey, userId, blob } of pendingImages) {
        try {
          const storagePath = `${userId}/${dateKey}.webp`;
          const { error: uploadError } = await supabase.storage
            .from('journal-images')
            .upload(storagePath, blob, { upsert: true, contentType: 'image/webp' });
          if (!uploadError) {
            const { data: { publicUrl } } = supabase.storage.from('journal-images').getPublicUrl(storagePath);
            const cur = stateRef.current.entries?.[dateKey];
            await upsertJournalEntry({
              user_id: userId,
              date_key: dateKey,
              summary: cur?.summary || null,
              entry: cur?.entry || null,
              drawing: cur?.drawing || null,
              image_url: publicUrl,
              is_favourite: cur?.isFavourite === true,
              location: cur?.location || null
            });
            await removePendingImage(dateKey);
          }
        } catch (e) {
          if (process.env.NODE_ENV === 'development') console.error('Offline image upload failed:', e);
        }
      }

      // Replay queued operations
      const ops = await getQueue();
      for (const op of ops) {
        try {
          if (op.type === 'journal_upsert') {
            await upsertJournalEntry(op.payload);
          } else if (op.type === 'habit_insert') {
            await supabase.from('habits').insert(op.payload);
          } else if (op.type === 'habit_reminder_update') {
            const { user_id, name, year_month, reminder_enabled, reminder_time } = op.payload;
            await supabase
              .from('habits')
              .update({ reminder_enabled, reminder_time })
              .eq('user_id', user_id)
              .eq('name', name)
              .eq('year_month', year_month);
          } else if (op.type === 'habit_visibility_update') {
            const { user_id, name, year_month, hide_from_friends } = op.payload;
            await supabase
              .from('habits')
              .update({ hide_from_friends })
              .eq('user_id', user_id)
              .eq('name', name)
              .eq('year_month', year_month);
          } else if (op.type === 'habit_card_color_update') {
            const { user_id, name, year_month, card_background_color } = op.payload;
            await supabase
              .from('habits')
              .update({ card_background_color })
              .eq('user_id', user_id)
              .eq('name', name)
              .eq('year_month', year_month);
          } else if (op.type === 'habit_delete') {
            await supabase.from('habits').delete().eq('user_id', op.payload.user_id).eq('name', op.payload.name).eq('year_month', op.payload.year_month);
            const [y, m] = op.payload.year_month.split('-');
            const daysInMonth = new Date(Number(y), Number(m), 0).getDate();
            for (let d = 1; d <= daysInMonth; d++) {
              const dk = `${y}-${m}-${String(d).padStart(2, '0')}`;
              await supabase.from('habit_log').delete().eq('user_id', op.payload.user_id).eq('habit_name', op.payload.name).eq('date_key', dk);
            }
          } else if (op.type === 'habit_log_upsert') {
            const { date_key, habit_name, completed } = op.payload;
            await supabase.from('habit_log').upsert({
              user_id: user.id,
              date_key,
              habit_name,
              completed,
              updated_at: new Date().toISOString()
            }, { onConflict: 'user_id,date_key,habit_name' });
            if (completed) awardXPForHabit(date_key, habit_name);
            else revokeXPForHabit(date_key, habit_name);
          } else if (op.type === 'sleep_upsert') {
            await supabase.from('sleep_log').upsert({ ...op.payload, updated_at: new Date().toISOString() }, { onConflict: 'user_id,date_key' });
          } else if (op.type === 'sleep_delete') {
            await supabase.from('sleep_log').delete().eq('user_id', op.payload.user_id).eq('date_key', op.payload.date_key);
          } else if (op.type === 'mood_upsert') {
            await supabase.from('mood_log').upsert({ ...op.payload, updated_at: new Date().toISOString() }, { onConflict: 'user_id,date_key' });
          } else if (op.type === 'weight_upsert') {
            await supabase.from('weight_log').upsert({ ...op.payload, updated_at: new Date().toISOString() }, { onConflict: 'user_id,date_key' });
          } else if (op.type === 'weight_delete') {
            await supabase.from('weight_log').delete().eq('user_id', op.payload.user_id).eq('date_key', op.payload.date_key);
          }
        } catch (e) {
          if (process.env.NODE_ENV === 'development') console.error('Offline sync op failed:', op, e);
        }
      }

      await clearQueue();
      await fetchAllData();
    } finally {
      setIsSyncing(false);
    }
  }, [user, isOnline, isSyncing, fetchAllData, awardXPForHabit, revokeXPForHabit, upsertJournalEntry]);

  useEffect(() => {
    if (isOnline && user) {
      flushQueue();
    }
  }, [isOnline, user, flushQueue]);

  // ===== UPDATE FUNCTIONS WITH SUPABASE SYNC =====

  // Update one-line summary
  const updateSummary = async (dateKey, summary) => {
    const sanitized = sanitizeText(summary, 2000);
    let merged;
    setEntries(prev => {
      merged = { ...(prev[dateKey] || {}), summary: sanitized };
      return { ...prev, [dateKey]: merged };
    });

    if (user) {
      const payload = { user_id: user.id, date_key: dateKey, summary: merged?.summary || null, entry: merged?.entry || null, drawing: merged?.drawing || null, image_url: merged?.imageUrl || null, is_favourite: merged?.isFavourite === true, location: merged?.location || null };
      if (isOnline) {
        await upsertJournalEntry(payload);
      } else {
        enqueue({ type: 'journal_upsert', payload }).catch(() => {});
      }
    }
  };

  // Update full journal entry
  const updateEntry = async (dateKey, entry) => {
    const sanitized = sanitizeText(entry, 50000);
    let merged;
    setEntries(prev => {
      merged = { ...(prev[dateKey] || {}), entry: sanitized };
      return { ...prev, [dateKey]: merged };
    });

    if (user) {
      const payload = { user_id: user.id, date_key: dateKey, summary: merged?.summary || null, entry: merged?.entry || null, drawing: merged?.drawing || null, image_url: merged?.imageUrl || null, is_favourite: merged?.isFavourite === true, location: merged?.location || null };
      if (isOnline) {
        await upsertJournalEntry(payload);
      } else {
        enqueue({ type: 'journal_upsert', payload }).catch(() => {});
      }
    }
  };

  // Update drawing for a journal entry
  const updateDrawing = async (dateKey, drawing) => {
    const sanitized = drawing != null ? sanitizeDrawing(drawing) : null;
    let merged;
    setEntries(prev => {
      merged = { ...(prev[dateKey] || {}), drawing: sanitized };
      return { ...prev, [dateKey]: merged };
    });

    if (user) {
      const payload = { user_id: user.id, date_key: dateKey, summary: merged?.summary || null, entry: merged?.entry || null, drawing: merged?.drawing || null, image_url: merged?.imageUrl || null, is_favourite: merged?.isFavourite === true, location: merged?.location || null };
      if (isOnline) {
        await upsertJournalEntry(payload);
      } else {
        enqueue({ type: 'journal_upsert', payload }).catch(() => {});
      }
    }
  };

  // Toggle favourite for a journal entry / memorable moment
  const toggleFavourite = async (dateKey) => {
    const current = entries[dateKey]?.isFavourite === true;
    const newFavourite = !current;

    let merged;
    setEntries(prev => {
      merged = { ...(prev[dateKey] || {}), isFavourite: newFavourite };
      return { ...prev, [dateKey]: merged };
    });

    if (user) {
      const payload = { user_id: user.id, date_key: dateKey, summary: merged?.summary || null, entry: merged?.entry || null, drawing: merged?.drawing || null, image_url: merged?.imageUrl || null, is_favourite: newFavourite, location: merged?.location || null };
      if (isOnline) {
        await upsertJournalEntry(payload);
      } else {
        enqueue({ type: 'journal_upsert', payload }).catch(() => {});
      }
    }
  };

  // Set or clear the location tag for a journal entry
  const updateLocation = async (dateKey, location) => {
    const sanitized = location ? sanitizeLocation(location) : null;
    let merged;
    setEntries(prev => {
      merged = { ...(prev[dateKey] || {}), location: sanitized };
      return { ...prev, [dateKey]: merged };
    });

    if (user) {
      const payload = { user_id: user.id, date_key: dateKey, summary: merged?.summary || null, entry: merged?.entry || null, drawing: merged?.drawing || null, image_url: merged?.imageUrl || null, is_favourite: merged?.isFavourite === true, location: sanitized };
      if (isOnline) {
        await upsertJournalEntry(payload);
      } else {
        enqueue({ type: 'journal_upsert', payload }).catch(() => {});
      }
    }
  };

  // Update image for a journal entry (upload to Storage, save URL to DB)
  const updateImageUrl = async (dateKey, file) => {
    if (!user || !file) return;

    const { compressImage, isValidImageType, isWithinSizeLimit } = await import('./imageUtils');
    if (!isValidImageType(file) || !isWithinSizeLimit(file)) {
      throw new Error('Invalid file. Use JPEG, PNG, or WebP under 10MB.');
    }

    const compressed = await compressImage(file);

    if (isOnline) {
      const storagePath = `${user.id}/${dateKey}.webp`;
      const { error: uploadError } = await supabase.storage
        .from('journal-images')
        .upload(storagePath, compressed, { upsert: true, contentType: 'image/webp' });

      if (uploadError) {
        if (process.env.NODE_ENV === 'development') console.error('Image upload error:', uploadError);
        throw new Error(uploadError.message || 'Failed to upload image.');
      }

      const { data: { publicUrl } } = supabase.storage.from('journal-images').getPublicUrl(storagePath);
      const refreshKey = Date.now();
      let merged;
      setEntries(prev => {
        merged = { ...(prev[dateKey] || {}), imageUrl: publicUrl, imageRefreshKey: refreshKey };
        return { ...prev, [dateKey]: merged };
      });

      await upsertJournalEntry({
        user_id: user.id, date_key: dateKey, summary: merged?.summary || null, entry: merged?.entry || null,
        drawing: merged?.drawing || null, image_url: publicUrl, is_favourite: merged?.isFavourite === true,
        location: merged?.location || null
      });
    } else {
      await savePendingImage(user.id, dateKey, compressed);
      const blobUrl = URL.createObjectURL(compressed);
      const refreshKey = Date.now();
      setEntries(prev => ({
        ...prev,
        [dateKey]: {
          ...(prev[dateKey] || {}),
          imageUrl: blobUrl,
          imageRefreshKey: refreshKey,
          _pendingImage: true
        }
      }));
    }
  };

  // Remove image from a journal entry
  const removeImageUrl = async (dateKey) => {
    if (!user) return;

    const current = entries[dateKey];
    const prevUrl = current?.imageUrl;

    if (current?._pendingImage && prevUrl?.startsWith?.('blob:')) {
      URL.revokeObjectURL(prevUrl);
    }

    let merged;
    setEntries(prev => {
      merged = { ...(prev[dateKey] || {}), imageUrl: null, _pendingImage: undefined };
      return { ...prev, [dateKey]: merged };
    });

    if (isOnline) {
      if (prevUrl && !prevUrl.startsWith('blob:')) {
        const storagePath = `${user.id}/${dateKey}.webp`;
        await supabase.storage.from('journal-images').remove([storagePath]);
      }
      await upsertJournalEntry({
        user_id: user.id, date_key: dateKey, summary: merged?.summary || null, entry: merged?.entry || null,
        drawing: merged?.drawing || null, image_url: null, is_favourite: merged?.isFavourite === true,
        location: merged?.location || null
      });
    } else {
      await removePendingImage(dateKey).catch(() => {});
      enqueue({ type: 'journal_upsert', payload: {
        user_id: user.id, date_key: dateKey, summary: merged?.summary || null, entry: merged?.entry || null,
        drawing: merged?.drawing || null, image_url: null, is_favourite: merged?.isFavourite === true,
        location: merged?.location || null
      } }).catch(() => {});
    }
  };

  const setHabitsMonth = useCallback((year, month) => {
    setHabitsYear(year);
    setHabitsMonthState(month);
  }, []);

  // Add a new habit for the current month only
  const addHabit = async (habitName) => {
    const sanitized = sanitizeShortText(habitName, 100);
    if (!sanitized) return;
    const key = currentYearMonthKey;
    const list = habitsByMonth[key] || [];
    if (list.includes(sanitized)) return;
    setHabitsByMonth((prev) => ({
      ...prev,
      [key]: [...(prev[key] || []), sanitized],
    }));

    if (user) {
      const payload = { user_id: user.id, name: sanitized, year_month: key };
      if (isOnline) {
        const { error } = await supabase.from('habits').insert(payload);
        if (error) {
          await fetchAllData();
          if (process.env.NODE_ENV === 'development') console.error('Failed to save habit:', error);
        }
      } else {
        enqueue({ type: 'habit_insert', payload }).catch(() => {});
      }
    }
  };

  // Remove a habit for the current month only (does not affect other months)
  const removeHabit = async (habitName) => {
    const key = currentYearMonthKey;
    const list = habitsByMonth[key] || [];
    if (!list.includes(habitName)) return;
    setHabitsByMonth((prev) => {
      const next = { ...prev };
      next[key] = (prev[key] || []).filter((h) => h !== habitName);
      return next;
    });

    setHabitsHideFromFriends((prev) => {
      const next = { ...prev };
      if (next[key]) {
        const copy = { ...next[key] };
        delete copy[habitName];
        next[key] = Object.keys(copy).length ? copy : undefined;
        if (!next[key]) delete next[key];
      }
      return next;
    });

    setHabitReminders((prev) => {
      const next = { ...prev };
      if (next[key]) {
        const copy = { ...next[key] };
        delete copy[habitName];
        next[key] = Object.keys(copy).length ? copy : undefined;
        if (!next[key]) delete next[key];
      }
      return next;
    });

    setHabitsBackgroundColors((prev) => {
      const next = { ...prev };
      if (next[key]) {
        const copy = { ...next[key] };
        delete copy[habitName];
        next[key] = Object.keys(copy).length ? copy : undefined;
        if (!next[key]) delete next[key];
      }
      return next;
    });

    setHabitLog((prev) => {
      const [y, m] = key.split("-");
      const prefix = `${y}-${m}-`;
      const updated = {};
      for (const dateKey in prev) {
        if (!dateKey.startsWith(prefix)) {
          updated[dateKey] = prev[dateKey];
          continue;
        }
        const dayLog = { ...prev[dateKey] };
        delete dayLog[habitName];
        updated[dateKey] = dayLog;
      }
      return updated;
    });

    if (user) {
      const payload = { user_id: user.id, name: habitName, year_month: key };
      if (isOnline) {
        await supabase.from('habits').delete().eq('user_id', user.id).eq('name', habitName).eq('year_month', key);
        const [y, m] = key.split("-");
        const daysInMonth = new Date(Number(y), Number(m), 0).getDate();
        for (let d = 1; d <= daysInMonth; d++) {
          const dk = `${y}-${m}-${String(d).padStart(2, "0")}`;
          await supabase.from('habit_log').delete().eq('user_id', user.id).eq('habit_name', habitName).eq('date_key', dk);
        }
      } else {
        enqueue({ type: 'habit_delete', payload }).catch(() => {});
      }
    }
  };

  // Update whether a habit is hidden from friends
  const updateHabitHideFromFriends = async (habitName, hideFromFriends) => {
    const key = currentYearMonthKey;
    const list = habitsByMonth[key] || [];
    if (!list.includes(habitName)) return;

    setHabitsHideFromFriends((prev) => {
      const next = { ...prev };
      if (!next[key]) next[key] = {};
      if (hideFromFriends) {
        next[key][habitName] = true;
      } else {
        delete next[key][habitName];
        if (Object.keys(next[key]).length === 0) delete next[key];
      }
      return next;
    });

    if (user) {
      const payload = { user_id: user.id, name: habitName, year_month: key, hide_from_friends: !!hideFromFriends };
      if (isOnline) {
        await supabase
          .from('habits')
          .update({ hide_from_friends: !!hideFromFriends })
          .eq('user_id', user.id)
          .eq('name', habitName)
          .eq('year_month', key);
      } else {
        enqueue({ type: 'habit_visibility_update', payload }).catch(() => {});
      }
    }
  };

  // Update habit reminder (notification time) for the current month
  const updateHabitReminder = async (habitName, { enabled, time }) => {
    const key = currentYearMonthKey;
    const list = habitsByMonth[key] || [];
    if (!list.includes(habitName)) return;

    setHabitReminders((prev) => {
      const next = { ...prev };
      if (!next[key]) next[key] = {};
      if (enabled && time) {
        next[key][habitName] = { enabled: true, time };
      } else {
        delete next[key][habitName];
        if (Object.keys(next[key]).length === 0) delete next[key];
      }
      return next;
    });

    if (user) {
      const payload = { user_id: user.id, name: habitName, year_month: key, reminder_enabled: !!enabled, reminder_time: enabled ? time : null };
      if (isOnline) {
        await supabase
          .from('habits')
          .update({ reminder_enabled: !!enabled, reminder_time: enabled ? time : null })
          .eq('user_id', user.id)
          .eq('name', habitName)
          .eq('year_month', key);
      } else {
        enqueue({ type: 'habit_reminder_update', payload }).catch(() => {});
      }
    }
  };

  // Habit card background color (mobile)
  const updateHabitBackgroundColor = async (habitName, color) => {
    const key = currentYearMonthKey;
    const list = habitsByMonth[key] || [];
    if (!list.includes(habitName)) return;

    const sanitized =
      color == null || color === "" ? null : sanitizeHabitCardColor(color);
    if (color != null && color !== "" && sanitized === undefined) return;

    setHabitsBackgroundColors((prev) => {
      const next = { ...prev };
      if (!next[key]) next[key] = {};
      if (sanitized != null) {
        next[key] = { ...next[key], [habitName]: sanitized };
      } else {
        const copy = { ...next[key] };
        delete copy[habitName];
        if (Object.keys(copy).length === 0) {
          const rest = { ...next };
          delete rest[key];
          return rest;
        }
        next[key] = copy;
      }
      return next;
    });

    if (user) {
      const payload = {
        user_id: user.id,
        name: habitName,
        year_month: key,
        card_background_color: sanitized,
      };
      if (isOnline) {
        await supabase
          .from("habits")
          .update({ card_background_color: sanitized })
          .eq("user_id", user.id)
          .eq("name", habitName)
          .eq("year_month", key);
      } else {
        enqueue({ type: "habit_card_color_update", payload }).catch(() => {});
      }
    }
  };

  // Toggle habit completion for a specific day
  const toggleHabit = async (dateKey, habitName) => {
    const currentValue = habitLog[dateKey]?.[habitName] || false;
    const newValue = !currentValue;

    setHabitLog(prev => {
      const dayLog = prev[dateKey] || {};
      return {
        ...prev,
        [dateKey]: {
          ...dayLog,
          [habitName]: newValue
        }
      };
    });

    if (user) {
      const payload = { user_id: user.id, date_key: dateKey, habit_name: habitName, completed: newValue };
      if (isOnline) {
        await supabase.from('habit_log').upsert({
          ...payload,
          updated_at: new Date().toISOString()
        }, { onConflict: 'user_id,date_key,habit_name' });
        if (newValue) awardXPForHabit(dateKey, habitName);
        else revokeXPForHabit(dateKey, habitName);
      } else {
        enqueue({ type: 'habit_log_upsert', payload }).catch(() => {});
      }
    }
  };

  // Update sleep hours for a specific day
  const updateSleep = async (dateKey, hours) => {
    const sanitized = hours !== undefined && hours !== null ? sanitizeSleep(hours) : undefined;
    if (hours !== undefined && hours !== null && sanitized === undefined) return;
    setSleepLog(prev => ({
      ...prev,
      [dateKey]: sanitized
    }));

    if (user) {
      if (isOnline) {
        if (sanitized === undefined || sanitized === null) {
          await supabase.from('sleep_log').delete().eq('user_id', user.id).eq('date_key', dateKey);
        } else {
          await supabase.from('sleep_log').upsert({
            user_id: user.id, date_key: dateKey, hours: sanitized,
            updated_at: new Date().toISOString()
          }, { onConflict: 'user_id,date_key' });
        }
      } else {
        if (sanitized === undefined || sanitized === null) {
          enqueue({ type: 'sleep_delete', payload: { user_id: user.id, date_key: dateKey } }).catch(() => {});
        } else {
          enqueue({ type: 'sleep_upsert', payload: { user_id: user.id, date_key: dateKey, hours: sanitized } }).catch(() => {});
        }
      }
    }
  };

  // Update mood for a specific day
  const updateMood = async (dateKey, mood) => {
    const sanitized = sanitizeMood(mood);
    setMoodLog(prev => ({
      ...prev,
      [dateKey]: sanitized
    }));

    if (user && sanitized !== undefined) {
      if (isOnline) {
        await supabase.from('mood_log').upsert({
          user_id: user.id, date_key: dateKey, mood: sanitized,
          updated_at: new Date().toISOString()
        }, { onConflict: 'user_id,date_key' });
      } else {
        enqueue({ type: 'mood_upsert', payload: { user_id: user.id, date_key: dateKey, mood: sanitized } }).catch(() => {});
      }
    }
  };

  // Delete all data for the given years via server-side RPC (requires network)
  const deleteYearData = async (yearsToDelete) => {
    if (!user || !yearsToDelete.length) return false;
    if (!isOnline) return false;

    try {
      const { data, error } = await supabase.rpc("delete_year_data", {
        p_years: yearsToDelete,
      });

      if (error) {
        if (process.env.NODE_ENV === "development") console.error("Error deleting year data:", error);
        return false;
      }

      await fetchAllData();
      return data?.deleted === true;
    } catch (err) {
      if (process.env.NODE_ENV === "development") console.error("Error deleting year data:", err);
      return false;
    }
  };

  // Update weight for a specific day
  const updateWeight = async (dateKey, weight) => {
    const sanitized = weight !== undefined && weight !== null ? sanitizeWeight(weight) : undefined;
    if (weight !== undefined && weight !== null && sanitized === undefined) return;
    setWeightLog(prev => ({
      ...prev,
      [dateKey]: sanitized
    }));

    if (user) {
      if (isOnline) {
        if (sanitized === undefined || sanitized === null) {
          await supabase.from('weight_log').delete().eq('user_id', user.id).eq('date_key', dateKey);
        } else {
          await supabase.from('weight_log').upsert({
            user_id: user.id, date_key: dateKey, weight: sanitized,
            updated_at: new Date().toISOString()
          }, { onConflict: 'user_id,date_key' });
        }
      } else {
        if (sanitized === undefined || sanitized === null) {
          enqueue({ type: 'weight_delete', payload: { user_id: user.id, date_key: dateKey } }).catch(() => {});
        } else {
          enqueue({ type: 'weight_upsert', payload: { user_id: user.id, date_key: dateKey, weight: sanitized } }).catch(() => {});
        }
      }
    }
  };

  return (
    <JournalContext.Provider
      value={{
        entries,
        updateSummary,
        updateEntry,
        updateDrawing,
        updateImageUrl,
        removeImageUrl,
        toggleFavourite,
        updateLocation,
        habits,
        habitsByMonth,
        habitsForMonth,
        habitsYear,
        habitsMonth,
        setHabitsMonth,
        addHabit,
        removeHabit,
        updateHabitReminder,
        updateHabitHideFromFriends,
        habitsHideFromFriends,
        habitLog,
        habitReminders,
        habitsBackgroundColors,
        updateHabitBackgroundColor,
        toggleHabit,
        sleepLog,
        updateSleep,
        moodLog,
        updateMood,
        weightLog,
        updateWeight,
        weightUnit,
        setWeightUnit,
        deleteYearData,
        isLoading,
        refreshData: fetchAllData,
        isOnline,
        isSyncing,
        pendingCount
      }}
    >
      {children}
    </JournalContext.Provider>
  );
};
